// =============================
// File: src/App.jsx (UPDATED)
// =============================
import React, { useState, useEffect, useLayoutEffect } from "react";
import { HashRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

// Keep your existing pages
import MainUser from "./user/MainUser";
import MainDriver from "./driver/MainDriver";
import MainAdmin from "./admin/MainAdmin";
import MainProvider from "./provider/MainProvider";
import MainFinalClient from "./finalClient/MainFinalClient";
import ConnectionHandler from "./ConnectionHandler";
import useTokenRefresh from "./useTokenRefresh";
import ProductChat from "./finalClient/chatbot/ProductChat";

import FastMaxLanding from "./web/FastMaxLanding";
import Login from "./web/Login";
import Register from "./web/Register";
import TermsAndConditions from "./web/TermsAndConditions";
import PrivacyPolicy from "./web/PrivacyPolicy";
import UserDocsViewer from "./user/UserDocsViewer";

const API = process.env.REACT_APP_API_URL || "";

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) {
      const id = hash.replace(/^#/, "");
      queueMicrotask(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "auto", block: "start" });
        } else {
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }
      });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}

export default function App() {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [userId, setUserId] = useState(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [role, setRole] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [providerId, setProviderId] = useState(null);

  const handleLoginSuccess = (user_id, username, email, phone_number, role, addresses = [], provider_id) => {
    setIsUserLoggedIn(true);
    setUserId(user_id);
    setUsername(username);
    setEmail(email);
    setPhoneNumber(phone_number);
    setRole(role);
    setAddresses(addresses || []);
    setProviderId(provider_id);
  };

  const handleUpdateUser = async () => {
    if (!userId) return;
    const response = await axios.get(`${API}/api/user/${userId}`);
    setUsername(response.data.username);
    setEmail(response.data.email);
    setPhoneNumber(response.data.phone_number);
    setRole(response.data.role);
    setProviderId(response.data.provider_id);
  };

  const handleLogout = () => {
    setIsUserLoggedIn(false);
    localStorage.clear();
  };

  // Restore session on mount
  useEffect(() => {
    const currentUrl = window.location.pathname;
    const isDeliveryRoute = currentUrl.includes("deliveryId");
    
    if (!isDeliveryRoute) {
      const token = localStorage.getItem("token");
      const storedUserId = localStorage.getItem("user_id");
      const storedUsername = localStorage.getItem("username");
      const storedEmail = localStorage.getItem("email");
      const storedPhoneNumber = localStorage.getItem("phone_number");
      const storedRole = localStorage.getItem("role");
      const storeProviderId = localStorage.getItem("providerId")
      let storedAddresses = [];
      try {
        storedAddresses = JSON.parse(localStorage.getItem("addresses")) || [];
      } catch (_) {}

      if (token && storedUserId && storedUsername && storedEmail && storedPhoneNumber && storedRole) {
        handleLoginSuccess(
          storedUserId,
          storedUsername,
          storedEmail,
          storedPhoneNumber,
          storedRole,
          storedAddresses,
          storeProviderId
        );
      }
    }
  }, []);

  useTokenRefresh(isUserLoggedIn);

  // Check if current URL is a docs route
  const currentHash = window.location.hash;
  const isDocsRoute = currentHash.includes('/user-docs') || 
                      currentHash.includes('/driver-docs') || 
                      currentHash.includes('/provider-docs');

  // If user is logged in BUT on a docs route, show docs instead of main app
  if (isUserLoggedIn && isDocsRoute) {
    return (
      <>
        <ConnectionHandler />
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/user-docs" element={<UserDocsViewer role="customer" />} />
            <Route path="/user-docs/:slug" element={<UserDocsViewer role="customer" />} />
            
            <Route path="/driver-docs" element={<UserDocsViewer role="driver" />} />
            <Route path="/driver-docs/:slug" element={<UserDocsViewer role="driver" />} />
            
            <Route path="/provider-docs" element={<UserDocsViewer role="provider" />} />
            <Route path="/provider-docs/:slug" element={<UserDocsViewer role="provider" />} />

            {/* Redirect everything else back to main app */}
            <Route path="*" element={<RedirectToMainApp />} />
          </Routes>
        </Router>
      </>
    );
  }

  return (
    <>
      <ConnectionHandler />
      {isUserLoggedIn ? (
        <div className="container-fluid p-0">
          {role === "customer" && (
            <MainUser onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} addresses={addresses} providerId={providerId} />
          )}
          {role === "driver" && (
            <MainDriver onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} />
          )}
          {role === "admin" && (
            <MainAdmin onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} />
          )}
          {role === "provider" && (
            <MainProvider onLogout={handleLogout} onUpdateUser={handleUpdateUser} user_id={userId} username={username} email={email} phone_number={phoneNumber} />
          )}
        </div>
      ) : (
        <Router>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<FastMaxLandingEntry onLoginSuccess={handleLoginSuccess} />} />
            <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
            <Route path="/register" element={<Register />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            
            {/* Documentation routes */}
            <Route path="/user-docs" element={<UserDocsViewer role="customer" />} />
            <Route path="/user-docs/:slug" element={<UserDocsViewer role="customer" />} />
            
            <Route path="/driver-docs" element={<UserDocsViewer role="driver" />} />
            <Route path="/driver-docs/:slug" element={<UserDocsViewer role="driver" />} />
            
            <Route path="/provider-docs" element={<UserDocsViewer role="provider" />} />
            <Route path="/provider-docs/:slug" element={<UserDocsViewer role="provider" />} />
            
            <Route path=":deliveryId" element={<MainFinalClient />} />
            <Route path="/chat/:sku/:sellerId" element={<ProductChat />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      )}
    </>
  );
}

// Helper component to redirect back to main app
function RedirectToMainApp() {
  useEffect(() => {
    // Close this window and focus back on main app window
    window.close();
    // If window.close() doesn't work (browser restrictions), redirect to home
    if (!window.closed) {
      window.location.href = '/#/';
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600 font-montserrat">Redirigiendo...</p>
      </div>
    </div>
  );
}

function FastMaxLandingEntry({ onLoginSuccess }) {
  const navigate = useNavigate();
  return <FastMaxLanding />;
}